import React, { useEffect } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { useSessionFocus } from "hooks/useSessionFocus";
import { groupFocusText } from "./studentReportTable";

/**
 * Column 2: what the group was working on during that session.
 *
 * The resolver is IMPORTED, never reimplemented (D48.13). It reconciles the
 * classroom's CURRENT assignment against the session's own snapshot, so a group
 * reassigned last week does not borrow a title from work it never did — and it
 * feeds the group-code cards and Session Reports too, so a copy here would be
 * exactly the drift its own docstring exists to prevent.
 *
 * THE WHOLE ROW IS HANDED TO THE HOOK, `learning_component_ids` included. Omit
 * that one field and nothing errors: the assignment match for a
 * component-filtered group silently goes false, the row falls back to its bare
 * snapshot, and the count line the design shows just disappears.
 *
 * TWO TREATMENTS, and the discriminator is `titledBy`, never the text of the
 * label: a STANDARD-titled row links its code, because a standard code is a
 * thing a teacher can go and look up. A LESSON-titled row draws it bold and
 * offers no link, because its title is not. Those two are the whole of
 * `titledBy` — a component filter no longer titles anything, it only adds the
 * count beside the code.
 */
export const GroupFocusCell = ({ row, onResolve }) => {
  const focus = useSessionFocus(row);
  // The resolver returns null rather than its own em dash for a session it
  // cannot name; the row's own snapshot is the honest fallback.
  const code = focus.code || row.standard_prefix || null;
  const title = focus.title && focus.title !== code ? focus.title : null;
  const isStandardTitled = focus.titledBy === "standard";
  // ONE string, assembled from the parts this cell is about to render, so the
  // CSV cannot spell the cell differently from the screen.
  const label = groupFocusText({ code, countLabel: focus.countLabel, title });

  useEffect(() => {
    if (onResolve) onResolve(row.session_id, label);
  }, [onResolve, row.session_id, label]);

  return (
    <>
      {code && isStandardTitled ? (
        <CodeLink
          data-testid="focus-code"
          href={`${STANDARD_LOOKUP_PATH}?code=${encodeURIComponent(code)}`}
          title={focus.codeTooltip || undefined}
          onClick={(event) => event.stopPropagation()}
        >
          {code}
        </CodeLink>
      ) : (
        code && (
          <CodeText data-testid="focus-code" title={focus.codeTooltip || undefined}>
            {code}
          </CodeText>
        )
      )}
      {focus.countLabel && <Count>{` · ${focus.countLabel}`}</Count>}
      {title && <Title data-testid="focus-title">{` ${title}`}</Title>}
    </>
  );
};

// The standards browser a teacher already has; the code is an OFFER to go and
// read the standard, which is the only reason the frame links it at all.
const STANDARD_LOOKUP_PATH = "/lessons-standards";

// One run of text, as the frame sets it (4485:72328): the code in Inter Bold,
// the title after it in Regular, both 16/22 in the table's own green, so the
// cell reads as a single line that wraps and clamps. The standard code keeps
// its link; it only stops being drawn as one until it is hovered.
const CodeLink = styled.a`
  color: ${colors.nightlyWoods500};
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: ${colors.nightlyWoods500};
    text-decoration: underline;
  }
`;

const CodeText = styled.span`
  color: ${colors.nightlyWoods500};
  font-weight: 700;
`;

const Count = styled.span`
  color: ${colors.nightlyWoods500};
`;

const Title = styled.span`
  color: ${colors.nightlyWoods500};
`;
